import type { KoraAccountIdentity } from './types'

export interface KoraProfileInput {
  readonly displayName?: string | null
  readonly email?: string | null
  readonly avatarUrl?: string | null
}

export interface SessionUserInput {
  readonly name?: string | null
  readonly email?: string | null
  readonly picture?: string | null
}

export interface AccountDisplay {
  readonly primary: string
  readonly secondary: string | null // email, omitted when equal to primary
  readonly initials: string // 1-2 code points, uppercase, '?' as the last resort
  readonly avatarSrc: string | null // https: only; null otherwise
}

function clean(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  // Zero-width and format characters (\p{Cf}) and the braille blank (U+2800) render as nothing, so
  // a name made only of them must count as empty.
  return trimmed.replace(/[\p{Cf}\u2800\s]/gu, '') === '' ? null : trimmed
}

/** Kora profile wins, then session claims. Null when there is no usable identity at all. */
export function resolveKoraAccountIdentity(
  kora: KoraProfileInput | null,
  session: SessionUserInput | null
): KoraAccountIdentity | null {
  const email = clean(kora?.email) ?? clean(session?.email)
  const displayName = clean(kora?.displayName) ?? clean(session?.name) ?? email
  if (displayName === null) return null
  return {
    displayName,
    email,
    avatarUrl: clean(kora?.avatarUrl) ?? clean(session?.picture)
  }
}

const LETTER_START = /^[^\p{L}\p{N}\p{Extended_Pictographic}]+/u

// Uppercase the symbol first, then keep one code point: 'ß' uppercases to 'SS' but the initial is 'S'.
function firstSymbol(word: string): string {
  const [first] = Array.from(word.replace(LETTER_START, ''))
  if (first === undefined) return ''
  const [initial] = Array.from(first.toLocaleUpperCase())
  return initial ?? ''
}

function isEmailLike(value: string): boolean {
  return /^[^\s@]+@[^\s@]+$/.test(value)
}

function deriveInitials(displayName: string): string {
  const trimmed = displayName.trim()
  const source = isEmailLike(trimmed) ? (trimmed.split('@')[0] ?? '') : trimmed
  const words = source.split(/\s+/).filter(word => word !== '')
  const first = words[0]
  const last = words[words.length - 1]
  const letters =
    first === undefined || last === undefined
      ? ''
      : words.length === 1
        ? firstSymbol(first)
        : firstSymbol(first) + firstSymbol(last)
  const initials = letters
  return initials === '' ? '?' : initials
}

function httpsAvatar(avatarUrl: string | null): string | null {
  if (avatarUrl === null) return null
  try {
    return new URL(avatarUrl).protocol === 'https:' ? avatarUrl : null
  } catch {
    return null
  }
}

/** What the menu shows when the host is signed in but has no identity yet. */
export function placeholderAccountDisplay(fallbackName: string): AccountDisplay {
  return { primary: fallbackName, secondary: null, initials: '?', avatarSrc: null }
}

export function deriveAccountDisplay(identity: KoraAccountIdentity): AccountDisplay {
  const primary = identity.displayName
  const email = identity.email
  return {
    primary,
    secondary: email !== null && email.toLowerCase() !== primary.toLowerCase() ? email : null,
    initials: deriveInitials(primary),
    avatarSrc: httpsAvatar(identity.avatarUrl)
  }
}
